/**
 * Internal (GHL-owned) agencies, by data-residency region.
 *
 * `isInternal` gates internal-only affordances in the builders — most visibly the payment test /
 * live mode toggle in the payment settings drawer. The allowlist is company ids, and company ids
 * do not cross cells: an AU cell has its own agencies, so the US list never matches there and the
 * toggle would be invisible to the AU internal team.
 *
 * A non-US region therefore uses its OWN list rather than extending the US one. With no region
 * (US / undefined / 'us') this is byte-identical to the hardcoded check it replaces.
 */
import { normalizeDrRegion } from './region'

/**
 * US ones
 * 5DP4iH6HLkQsiKESj6rh (staging), YuTUZlUtrwBtvmgByZDW (production),
 * ktapXmiRvMM4pJHyN3b3 (production), pfpm9iFi4k5yvjnstXWU (production)
 */
export const INTERNAL_COMPANY_IDS = ['5DP4iH6HLkQsiKESj6rh', 'YuTUZlUtrwBtvmgByZDW', 'ktapXmiRvMM4pJHyN3b3', 'pfpm9iFi4k5yvjnstXWU']

/**
 * Per-region internal agencies. A region listed here REPLACES the US list for that cell.
 * A region absent from this map falls through to `INTERNAL_COMPANY_IDS`.
 */
export const REGION_INTERNAL_COMPANY_IDS: Record<string, string[]> = {
  au: ['hfNhl3Ds0WNsDFLjtdPo', 'pGcN3H2ZrCUcUV0nCZfp'],
}

/** Internal agencies for a region. The US list for US / missing / an unmapped region. */
export const getInternalCompanyIds = (region?: string | null): string[] => {
  const normalized = normalizeDrRegion(region)
  if (normalized) {
    const regionIds = REGION_INTERNAL_COMPANY_IDS[normalized]
    if (regionIds && regionIds.length > 0) {
      return regionIds
    }
  }
  return INTERNAL_COMPANY_IDS
}

/** Is this company one of the region's internal agencies? */
export const isInternalCompany = (companyId?: string | null, region?: string | null): boolean => {
  if (!companyId) {
    return false
  }
  return getInternalCompanyIds(region).includes(companyId)
}
